import React from 'react';
import { Modal, Card, Divider } from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { FlexWrapper } from '../../theme/common_style';
import { getFullName } from '../../utils/common_functions';
import { CustomCheckbox } from './CreateLeads';
import { followUp } from '../../utils/constant';
import { useResponsiveModalWidth } from '../../hooks/useResponsiveModalWidth';

const Wrapper = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;

  .ant-card-body {
    padding: 20px;
    @media (max-width: 600px) {
      padding: 10px !important;
    }
  }
`;

const GreyText = styled.p`
  color: ${({ color }) => color || '#9f9f9f !important'};
  font-size: ${({ size }) => size || '14px'};
  margin: ${({ margin }) => margin || '0'};
  font-family: Plus Jakarta Sans;
  font-weight: 500;
  text-wrap: wrap;
`;

const PersonInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  .person-details {
    display: flex;

    .name {
      font-weight: 400;
      color: #262626;
    }
  }
`;

const NoteSection = styled.div`
  margin-top: 20px;

  .note-label {
    font-weight: 500;
    color: #262626;
    margin-bottom: 8px;
    display: block;
  }

  .note-content {
    background-color: #fafafa;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
    color: #595959;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const ViewLeads = ({ open, onCancel, data }) => {
  const { date, owner, billing_id_name, client_name, job_title, prop_url, country } = data || {};

  const width = useResponsiveModalWidth(800);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('DD MMM, YYYY');
  };

  return (
    <Modal
      open={open}
      maskClosable={false}
      onCancel={onCancel}
      centered
      prefixCls="antCustomModal"
      destroyOnClose
      width={width}
      footer={false}>
      <Title level={4} style={{ margin: '0 0 20px 0', textAlign: 'center' }}>
        View Lead
      </Title>

      <Wrapper>
        <FlexWrapper justify="start" margin="0 0 16px">
          <GreyText>Date:</GreyText>
          <span style={{ marginLeft: '8px', fontWeight: 500 }}>{formatDate(date)}</span>
        </FlexWrapper>

        <GridBox cols={2} gap="8px">
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Lead Manager:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">
                  {owner?.first_name ||
                    getFullName(owner?.first_name, owner?.middle_name, owner?.last_name)}
                </span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>ID Name:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">{billing_id_name?.name}</span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Client Name:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">{client_name}</span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Job Title:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">{job_title}</span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Proposal:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">
                  <a href={prop_url}>click to view</a>
                </span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>country:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">{country}</span>
              </div>
            </PersonInfo>
          </FlexWrapper>
        </GridBox>

        <Divider />

        <GridBox cols={2} gap="8px">
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Business Manager:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">
                  {data?.business_manager ? data?.business_manager?.name : 'N/A'}
                </span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Call By:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">
                  {data?.call
                    ? getFullName(
                        data?.call?.first_name,
                        data?.call?.middle_name,
                        data?.call?.last_name
                      )
                    : 'N/A'}
                </span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Plateform:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">
                  {data?.platform?.length > 0 ? data?.platform?.join(', ') : 'N/A'}
                </span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Estimation:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">{data?.estimation || 'N/A'}</span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Sent Estimation time:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">
                  {data?.give_estimation
                    ? dayjs(data?.give_estimation).format('YYYY-MM-DD hh:mm A')
                    : 'N/A'}
                </span>
              </div>
            </PersonInfo>
          </FlexWrapper>
          <FlexWrapper align="center" gap="8px" justify="start">
            <GreyText>Received Estimation time:</GreyText>
            <PersonInfo>
              <div className="person-details">
                <span className="name">
                  {data?.get_estimation
                    ? dayjs(data?.get_estimation).format('YYYY-MM-DD hh:mm A')
                    : 'N/A'}
                </span>
              </div>
            </PersonInfo>
          </FlexWrapper>
        </GridBox>

        <Divider />

        <FlexWrapper align="center" gap="8px" justify="start">
          <GreyText>Real Time Status:</GreyText>
          <PersonInfo>
            <div className="person-details">
              <span className="name">
                {data?.real_time_status ? data?.real_time_status : 'N/A'}
              </span>
            </div>
          </PersonInfo>
        </FlexWrapper>

        <FlexWrapper align="center" gap="8px" justify="start" margin="10px 0">
          <GreyText>Has the module sheet been sent to the client?</GreyText>
          <PersonInfo>
            <div className="person-details">
              <span className="name">
                <CustomCheckbox defaultChecked={data?.module_sheet_send} disabled></CustomCheckbox>
              </span>
            </div>
          </PersonInfo>
        </FlexWrapper>

        <FlexWrapper align="start" gap="5px" justify="start" direction="column">
          <GreyText>Follow Up:</GreyText>
          <PersonInfo>
            <div className="person-details">
              <span className="name">
                {followUp?.map((el) => (
                  <CustomCheckbox defaultChecked={data?.days?.includes(el)} disabled key={el}>
                    {el}
                  </CustomCheckbox>
                ))}
              </span>
            </div>
          </PersonInfo>
        </FlexWrapper>

        <Divider />

        <NoteSection>
          <span className="note-label">Note:</span>
          <div className="note-content">{data?.note || 'No additional notes provided.'}</div>
        </NoteSection>
      </Wrapper>
    </Modal>
  );
};

export default ViewLeads;

ViewLeads.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  data: PropTypes.any
};

const GridBox = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols}, 1fr)`};
  gap: ${({ gap }) => gap || '16px'};
  @media (max-width: 620px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;
